class AutoLoadItems extends HTMLElement {
  constructor() {
    super()

    this.pagination = this.querySelector('[data-pagination]')
    this.trigger = this.querySelector('[data-trigger]')
    this.items = this.querySelector('[data-items]')
    this.observer = null
    this.pagesLoaded = 1

    if (this.pagination && this.items) {
      this.currentPage = parseInt(this.pagination.children[0].dataset.page)
      this.maxPages = parseInt(this.pagination.children[0].dataset.maxPages)
      this.pagination.classList.add('hidden')

      if (this.currentPage < this.maxPages) {
        this.observer = new IntersectionObserver(this.intersectionCallback.bind(this), {})
        this.observer.observe(this.trigger)
      }
    }

    if (this.items) {
      this.items.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) this.saveScrollState()
      })
    }

    this.restoreScrollState()
  }

  get stateKey() {
    return 'collection_scroll_state'
  }

  saveScrollState() {
    try {
      sessionStorage.setItem(this.stateKey, JSON.stringify({
        url: location.pathname + location.search,
        scrollY: window.scrollY,
        pagesLoaded: this.pagesLoaded,
      }))
    } catch (_) {}
  }

  restoreScrollState() {
    try {
      const raw = sessionStorage.getItem(this.stateKey)
      if (!raw) return

      const state = JSON.parse(raw)
      sessionStorage.removeItem(this.stateKey)

      if (state.url !== location.pathname + location.search) return
      if (!this.pagination || !this.items) return

      // Pause normal infinite-scroll while restoring
      if (this.observer) this.observer.unobserve(this.trigger)

      const targetPage = Math.min(state.pagesLoaded, this.maxPages)

      this.loadPagesSequentially(targetPage).then(() => {
        // Double rAF: first frame commits layout, second ensures it is painted
        requestAnimationFrame(() => requestAnimationFrame(() => {
          window.scrollTo({ top: state.scrollY, behavior: 'instant' })
        }))

        if (this.currentPage < this.maxPages && this.observer) {
          this.observer.observe(this.trigger)
        }
      })
    } catch (_) {}
  }

  async loadPagesSequentially(targetPage) {
    while (true) {
      this.pagination = this.querySelector('[data-pagination]')
      this.currentPage = parseInt(this.pagination.children[0].dataset.page)
      this.maxPages = parseInt(this.pagination.children[0].dataset.maxPages)

      if (this.currentPage >= targetPage || this.currentPage >= this.maxPages) break

      const nextPageUrl = this.pagination.children[0].children[this.currentPage].children[0].href
      await this.fetchPosts(nextPageUrl)
      this.pagesLoaded++
    }
  }

  loadItems() {
    if (this.classList.contains('loading')) return

    this.pagination = this.querySelector('[data-pagination]')
    this.currentPage = parseInt(this.pagination.children[0].dataset.page)
    this.maxPages = parseInt(this.pagination.children[0].dataset.maxPages)

    if (this.currentPage === this.maxPages) {
      this.observer.unobserve(this.trigger)
      return
    }

    this.classList.add('loading')

    const nextPageUrl = this.pagination.children[0].children[this.currentPage].children[0].href

    this.fetchPosts(nextPageUrl).then(() => {
      this.pagesLoaded++
    })
  }

  fetchPosts(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const itemsLoaded = doc.querySelector('[data-items]')
        const paginationLoaded = doc.querySelector('[data-pagination]')

        Array.from(itemsLoaded.children).forEach((item) => {
          this.items.appendChild(item)
        })
        this.pagination.innerHTML = paginationLoaded.innerHTML
      })
      .catch((err) => {
        console.warn('Something went wrong.', err)
      })
      .finally(() => {
        this.classList.remove('loading')
      })
  }

  intersectionCallback(entries) {
    if (entries.length > 0) {
      const entry = entries[0]
      if (entry.isIntersecting) {
        this.loadItems()
      }
    }
  }
}

customElements.define('auto-load-items', AutoLoadItems)
